import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  PhotoGalleriesFilterParams,
  PhotoGalleryType,
  PhotoGalleriesListResponse,
  CreatePhotoGalleryParams,
  UpdatePhotoGalleryParams
} from '@/types/photo-gallery'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

// API functions
import {
  fetchPhotoGalleries,
  fetchPhotoGallery,
  createPhotoGallery,
  updatePhotoGallery,
  deletePhotoGallery,
  bulkDeletePhotoGalleries,
  sortPhotoGallery
} from '@/lib/api'

type PhotoGalleryStateType = {
  data: PhotoGalleryType | null
  isLoading: boolean
  error: string | null
}

type PhotoGalleriesListStateType = {
  data: PhotoGalleriesListResponse | null
  isLoading: boolean
  error: string | null
}

type PhotoGalleriesStoreState = {
  photoGallery: PhotoGalleryStateType
  photoGalleriesList: PhotoGalleriesListStateType
  filters: PhotoGalleriesFilterParams
  selectedPhotoGalleryIds: string[]
  setFilters: (filters: Partial<PhotoGalleriesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchPhotoGalleries: (params?: PhotoGalleriesFilterParams) => Promise<PhotoGalleriesListResponse>
  resetPhotoGalleriesList: () => void

  // Sort photo gallery
  sortPhotoGallery: (id: string, type: SortOrderEnum) => Promise<PhotoGalleryType>

  // Blog Post operations
  getPhotoGallery: (id: string) => Promise<PhotoGalleryType>
  createPhotoGallery: (params: CreatePhotoGalleryParams) => Promise<PhotoGalleryType>
  updatePhotoGallery: (id: string, params: UpdatePhotoGalleryParams) => Promise<PhotoGalleryType>
  deletePhotoGallery: (id: string) => Promise<void>
  bulkDeletePhotoGalleries: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectPhotoGallery: (id: string) => void
  deselectPhotoGallery: (id: string) => void
  togglePhotoGallerySelection: (id: string) => void
  selectAllPhotoGalleries: (photoGalleries: PhotoGalleryType[]) => void
  deselectAllPhotoGalleries: () => void
  isPhotoGallerySelected: (id: string) => boolean
  resetPhotoGallery: () => void
}

const defaultFilters: PhotoGalleriesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'order',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined,
  isBlocked: undefined
}

const initialPhotoGalleryState: PhotoGalleryStateType = {
  data: null,
  isLoading: false,
  error: null
}

const initialPhotoGalleriesListState: PhotoGalleriesListStateType = {
  data: null,
  isLoading: false,
  error: null
}

const usePhotoGalleriesStore = create<PhotoGalleriesStoreState>()(
  devtools((set, get) => ({
    photoGallery: initialPhotoGalleryState,
    photoGalleriesList: initialPhotoGalleriesListState,
    selectedPhotoGalleryIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof PhotoGalleriesFilterParams]
          } else {
            updatedFilters[key as keyof PhotoGalleriesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: PhotoGalleriesFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as PhotoGalleriesFilterParams['sortBy']
        } else if (key === 'isBlocked') {
          filters[key] = value === 'true'
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof PhotoGalleriesFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getPhotoGallery: async id => {
      set(state => ({
        photoGallery: {
          ...state.photoGallery,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchPhotoGallery(id)

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createPhotoGallery: async params => {
      set(state => ({
        photoGallery: {
          ...state.photoGallery,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createPhotoGallery(params)

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updatePhotoGallery: async (id, params) => {
      set(state => ({
        photoGallery: {
          ...state.photoGallery,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updatePhotoGallery(id, params)

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deletePhotoGallery: async id => {
      set(state => ({
        photoGallery: {
          ...state.photoGallery,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deletePhotoGallery(id)

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetPhotoGallery: () => {
      set(state => ({
        photoGallery: {
          ...state.photoGallery,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectPhotoGallery: id => {
      set(state => {
        if (state.selectedPhotoGalleryIds.includes(id)) {
          return state
        }
        return { selectedPhotoGalleryIds: [...state.selectedPhotoGalleryIds, id] }
      })
    },

    deselectPhotoGallery: id => {
      set(state => ({
        selectedPhotoGalleryIds: state.selectedPhotoGalleryIds.filter(photoGalleryId => photoGalleryId !== id)
      }))
    },

    togglePhotoGallerySelection: id => {
      set(state => {
        if (state.selectedPhotoGalleryIds.includes(id)) {
          return {
            selectedPhotoGalleryIds: state.selectedPhotoGalleryIds.filter(photoGalleryId => photoGalleryId !== id)
          }
        }
        return { selectedPhotoGalleryIds: [...state.selectedPhotoGalleryIds, id] }
      })
    },

    selectAllPhotoGalleries: photoGalleries => {
      set(() => ({
        selectedPhotoGalleryIds: photoGalleries.map(photoGallery => photoGallery.id)
      }))
    },

    deselectAllPhotoGalleries: () => {
      set(() => ({ selectedPhotoGalleryIds: [] }))
    },

    isPhotoGallerySelected: id => {
      return get().selectedPhotoGalleryIds.includes(id)
    },

    // Bulk delete operation
    bulkDeletePhotoGalleries: async ids => {
      set(state => ({
        photoGallery: {
          ...state.photoGallery,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeletePhotoGalleries({ ids })

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedPhotoGalleryIds: state.selectedPhotoGalleryIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          photoGallery: {
            ...state.photoGallery,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchPhotoGalleries: async (params?: PhotoGalleriesFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        photoGalleriesList: {
          ...state.photoGalleriesList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchPhotoGalleries(filtersToUse)

        set(state => ({
          photoGalleriesList: {
            ...state.photoGalleriesList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          photoGalleriesList: {
            ...state.photoGalleriesList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetPhotoGalleriesList: () => {
      set(state => ({
        photoGalleriesList: {
          ...state.photoGalleriesList,
          data: null,
          error: null
        }
      }))
    },

    sortPhotoGallery: async (id, type) => {
      set(() => ({
        photoGallery: {
          ...get().photoGallery,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await sortPhotoGallery(id, type)

        set(() => ({
          photoGallery: {
            ...get().photoGallery,
            isLoading: false,
            data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          photoGallery: {
            ...initialPhotoGalleryState,
            error: getErrorMessage(error)
          }
        }))
        throw error
      }
    }
  }))
)

export default usePhotoGalleriesStore
